<script setup>
import { ref, onMounted, watch, computed, onBeforeUnmount, reactive, nextTick } from 'vue';
import AppLoader from '../../components/AppLoader.vue';
import PageBreadcrumb from '../../components/PageBreadcrumb.vue';
import AppFooter from '@/components/AppFooter.vue';
import { useSourcesStore } from '../../stores/sources.store';
import { useDestinationsStore } from '../../stores/destinations.store';
import { usePipelinesStore } from '../../stores/pipelines.store';
import { useTransformsStore } from '../../stores/transforms.store';
import { useMetricsStore } from '@/stores/metrics.store';
import { useTagsStore } from '@/stores/tags.store';
import { useAuthStore } from '@/stores/auth.store';
import ErrorMessage from '../../components/ErrorMessage.vue';
import TreeView from '../../components/TreeView.vue';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import _ from 'lodash';
import ActionMenu from '@/components/ActionMenu.vue';
import StatCard from '@/components/global/StatCard.vue';
import { SkeletonLoader } from '@/components/ui/skeleton-loader';
import { toast } from '@/composables/useToast';
import { useUrlFilters } from '@/composables/useUrlFilters';
import { FILTER_DEFAULTS } from '@/utils/constants';
import { userHasAccess, getGlobalConfigValue, getBadgeVariantAndClasses } from '../../utils/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { TAG_BADGE_COLOR_CLASSES_PER_TYPE, TAG_TYPES } from '@/utils/constants';
import PeriodicAuditConfig from '@/components/pipelines/PeriodicAuditConfig.vue';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableEmpty } from '@/components/ui/table';
import { TableEmptyState } from '@/components/ui/table-empty-state';
import { Button } from '@/components/ui/button';

import { Info, ChevronRight, CheckCircle, Cpu, SlidersVertical, HelpCircle, Search, Clock, List, LoaderCircle } from 'lucide-vue-next';

import { buildTreeNodesOutOfExistingConfig, getStatusColor, formatter, compareWithOmittedProperties } from '../../utils/utils';
import { getConnectorIcon, getConnectorDisplayName, MAX_ONE_TAG_PER_TYPE } from '@/utils/constants';
import Tabs from '@/components/global/Tabs.vue';
import ConnectGuideTab from '@/components/global/ConnectGuideTab.vue';
import { useErrorDialog } from '@/composables/useDialog';
import { useDeleteConfirmation, useConfirmDialog } from '@/composables/useDialog';
import { MultiSelect } from '@/components/ui/multi-select';
import WebSocketService from '../../services/webSocket.service';
import YesNoConfirmModal from '@/components/global/modals/YesNoConfirmModal.vue';

const props = defineProps(['id', 'customMessage', 'source', 'destination']);
const pipelinesStore = usePipelinesStore();
const sourcesStore = useSourcesStore();
const destinationsStore = useDestinationsStore();
const tagsStore = useTagsStore();
const transformsStore = useTransformsStore();
const metricsStore = useMetricsStore();
const authStore = useAuthStore();

const { registerFilter } = useUrlFilters({
    defaults: {
        tab: FILTER_DEFAULTS.TABS.STATUS
    }
});

let pipeline = ref(null);
let pipelineSource = ref(null);
let pipelineSourceConfig = ref(null);
let router = useRouter();
let route = useRoute();

// Breadcrumb management
const { setDynamicLabel, clearDynamicLabel } = useBreadcrumbs();

let generalLoading = ref(false);
let actionLoading = ref(false);
let configurationSchemaLoading = ref(false);
let tagsLoading = ref(false);
let tags = ref([]);
let transformsLoading = ref(false);

let defaultTransformsTree = ref([]);
let defaultSchemaTree = ref([]);
let hidePromptTemporary = ref(false);
let isTopicsConfirmModalOpen = ref(false);

let formError = ref('');
let treeLevels = ref([]);
let treeNodes = ref([]);
let transformTreeNodes = ref([]);
let transformTreeLevels = ref([]);
let metricsLoading = ref(false);
let snapshotNewTables = ref(true);
let periodicAuditConfig = ref({
    enabled: false,
    topics: [],
    timestampColumn: '',
    intervalMinutes: null
});
let defaultPipelineName = ref('');
let defaultPipelineTags = ref([]);

let auditValid = ref(true);

watch(
    periodicAuditConfig,
    () => {
        // re-trigger tree-validity logic when audit config changes
        auditValid.value =
            !periodicAuditConfig.value.enabled ||
            (periodicAuditConfig.value.topics.length > 0 &&
                periodicAuditConfig.value.timestampColumn &&
                periodicAuditConfig.value.intervalMinutes > 0);
    },
    { deep: true, immediate: true }
);

// Update breadcrumb label when pipeline name becomes available
watch(
    () => pipeline.value?.name,
    (newName) => {
        if (newName) {
            setDynamicLabel('pipeline-details', newName);
        }
    },
    { immediate: true }
);

/** detect if audit config diverged from default */
let defaultAuditConfig = ref(null);

let selectedTags = ref([]);

const selectedTagIds = computed({
    get() {
        return selectedTags.value.map((tag) => tag.id);
    },
    set(ids) {
        selectedTags.value = ids.map((id) => tags.value.find((tag) => tag.id === id)).filter(Boolean);
    }
});

let canSaveTree = ref(true);
let areTreesEqual = ref(false);

const tabs = computed(() => {
    const baseTabs = [
        { name: 'Status', href: '#', current: true },
        { name: 'Settings', href: '#', current: false }
    ];

    // Add Connect Guide tab if connect_guide data exists
    if (pipeline.value?.connect_guide) {
        baseTabs.push({
            name: 'Connect Guide',
            key: 'connect-guide',
            href: '#',
            current: false
        });
    }

    // Add external connector tabs
    baseTabs.push(
        {
            name: 'Source',
            href: `/connectors/sources/${route.query.source || pipeline.value?.source?.id || ''}`,
            target: '_blank',
            rel: 'noopener noreferrer',
            newTabUrl: true,
            current: false
        },
        {
            name: 'Destination',
            href: `/connectors/destinations/${route.query.destination || pipeline.value?.destination?.id || ''}`,
            target: '_blank',
            rel: 'noopener noreferrer',
            newTabUrl: true,
            current: false
        }
    );

    return baseTabs;
});

const showStopSource = computed(() => {
    return (
        pipeline.value?.source.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.source.connector_status?.toLowerCase() === 'paused' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'starting' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'stopped'
        )
    );
});

const validationClass = computed(() => {
    if (!tagsLoading.value && !actionLoading.value && !generalLoading.value && tags.value?.length && !selectedTags.value.length) {
        return 'border-destructive!'; // Tailwind's red border class
    }

    return 'border-border'; // Default border class
});

const showResumeSource = computed(() => {
    return (
        pipeline.value?.source.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.source.connector_status?.toLowerCase() === 'active' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'broken' ||
            pipeline.value?.source.connector_status?.toLowerCase() === 'starting'
        )
    );
});

const showStopDestination = computed(() => {
    return (
        pipeline.value?.destination.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.destination.connector_status?.toLowerCase() === 'paused' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'starting' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'stopped'
        )
    );
});

const showResumeDestination = computed(() => {
    return (
        pipeline.value?.destination.connector !== 'kafkadirect' &&
        !(
            pipeline.value?.destination.connector_status?.toLowerCase() === 'active' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'broken' ||
            pipeline.value?.destination.connector_status?.toLowerCase() === 'starting'
        )
    );
});

/** Audit feature available only for mysql->snowflake or postgresql->clickhouse pipelines */
const isAuditAllowed = computed(() => {
    return pipeline.value.allow_audit;
});

// const onPauseDestination = async () => {
//     try {
//         actionLoading.value = true;
//         await destinationsStore.pause(pipeline.value.destination.id, true);
//         actionLoading.value = false;
//         toast.success('Destination paused successfully.');

//         metricsLoading.value = true;
//         const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
//         pipeline.value = promises[1];
//         metricsLoading.value = false;
//     } catch (e) {
//         toast.error('There was an error while pausing the destination. Please try again.');
//     } finally {
//         actionLoading.value = false;
//         metricsLoading.value = false;
//     }
// };

const onStopDestination = async () => {
    try {
        actionLoading.value = true;
        await destinationsStore.stop(pipeline.value.destination.id, true);
        actionLoading.value = false;
        toast.success('Destination stopped successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];
        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while stopping the destination. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const onResumeDestination = async () => {
    try {
        actionLoading.value = true;
        await destinationsStore.resume(pipeline.value.destination.id, true);
        actionLoading.value = false;
        toast.success('Destination resumed successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];
        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while resuming the destination. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const onStopSource = async () => {
    try {
        actionLoading.value = true;
        await sourcesStore.stop(pipeline.value.source.id, true);
        actionLoading.value = false;
        toast.success('Source stopped successfully.');

        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];

        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while stopping the source. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

// const onPauseSource = async () => {
//     try {
//         actionLoading.value = true;
//         await sourcesStore.pause(pipeline.value.source.id, true);
//         actionLoading.value = false;
//         toast.success('Source paused successfully.');

//         metricsLoading.value = true;
//         const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
//         pipeline.value = promises[1];

//         metricsLoading.value = false;
//     } catch (e) {
//         toast.error('There was an error while pausing the source. Please try again.');
//     } finally {
//         actionLoading.value = false;
//         metricsLoading.value = false;
//     }
// };

const onResumeSource = async () => {
    try {
        actionLoading.value = true;
        await sourcesStore.resume(pipeline.value.source.id, true);
        actionLoading.value = false;

        toast.success('Source resumed successfully.');
        metricsLoading.value = true;
        const promises = await Promise.all([metricsStore.pipelines.fetch(pipeline.value.id), await pipelinesStore.getById(props.id)]);
        pipeline.value = promises[1];

        metricsLoading.value = false;
    } catch (e) {
        toast.error('There was an error while resuming the source. Please try again.');
    } finally {
        actionLoading.value = false;
        metricsLoading.value = false;
    }
};

const pipelineLatestMetrics = computed(() => {
    return metricsStore.pipelines.allLatest[pipeline.value?.id];
});

const stats = computed(() => {
    return [
        {
            id: 0,
            name: 'Status',
            stat: pipelineLatestMetrics.value?.inline?.status ?? '-',
            icon: CheckCircle,
            colorClass: getStatusColor(pipelineLatestMetrics.value?.inline?.status, false, true)
        },
        {
            id: 2,
            name: 'Topics',
            stat: pipeline.value.topics.length,
            icon: List,
            colorClass: 'bg-primary'
        },
        {
            id: 4,
            name: 'Latency',
            icon: Clock,
            colorClass: 'bg-primary/50',
            stat: getFormattedValue('latency', 'milliseconds').display,
            hasHighLatency: pipelineLatestMetrics.value?.inline?.latency >= 60000
        },
        {
            id: 4,
            name: 'Lag',
            stat: getFormattedValue('recordsLag', 'count').display,
            icon: Cpu,
            colorClass: 'bg-chart-4'
        }
    ];
});

const formattedTopicsAndTransforms = computed(() => {
    // Helper function to find matching full topic_id from source
    const findFullTopicId = (pipelineTopicId) => {
        if (!pipelineSource.value?.topic_ids) return pipelineTopicId;

        // DynamoDB-specific handling for .default. intermediate
        if (pipelineSource.value.connector === 'dynamodb') {
            const sourceTopicId = pipelineSource.value.topic_ids.find((sourceId) => {
                // Remove .default. intermediate from source for comparison
                const sourceWithoutDefault = sourceId.replace(/\.default\./, '.');

                // Case 1: Pipeline topic has no dots (e.g., "demo" vs "default.demo")
                if (!pipelineTopicId.includes('.')) {
                    return sourceId.endsWith('.' + pipelineTopicId) || sourceWithoutDefault.endsWith('.' + pipelineTopicId);
                }

                // Case 2: Pipeline topic has dots (e.g., "source_xyz.demo" vs "source_xyz.default.demo")
                const pipelineSuffix = pipelineTopicId.split('.').slice(1).join('.');
                return (
                    sourceId.endsWith('.' + pipelineSuffix) ||
                    sourceWithoutDefault.endsWith('.' + pipelineSuffix) ||
                    sourceWithoutDefault === pipelineTopicId
                );
            });

            return sourceTopicId || pipelineTopicId;
        }

        // General logic for other connectors
        const sourceTopicId = pipelineSource.value.topic_ids.find((sourceId) => {
            // Extract the suffix pattern (everything after the first dot)
            const pipelineSuffix = pipelineTopicId.split('.').slice(1).join('.');
            return sourceId.endsWith('.' + pipelineSuffix);
        });

        return sourceTopicId || pipelineTopicId;
    };

    const transforms = _.map(pipeline.value.transforms, (transform) => {
        if (transform.topic_id) {
            const fullTopicId = findFullTopicId(transform.topic_id);
            const split = fullTopicId.split('.');
            const head = split.shift();

            // Clean topic name for display (remove .default for DynamoDB only if original pipeline topic doesn't contain .default)
            let displayName = split.length ? split.join('.') : head;
            if (pipelineSource.value?.connector === 'dynamodb' && split.length > 0 && split[0] === 'default') {
                // Only strip .default if the original pipeline topic_id doesn't also contain .default
                const originalPipelineTopicContainsDefault = transform.topic_id && transform.topic_id.includes('.default');
                if (!originalPipelineTopicContainsDefault) {
                    displayName = split.slice(1).join('.') || head;
                }
            }

            return {
                id: split.length ? head : '-',
                name: displayName,
                topic_id: fullTopicId,
                transform_name: transform.name,
                transform: true
            };
        } else {
            return {
                id: transform.id,
                name: transform.name,
                transform: true
            };
        }
    });

    const transformIds = _.map(
        transforms.filter((t) => t.topic_id),
        (transform) => transform.topic_id
    );

    const topics = _.map(
        pipeline.value.topic_ids.filter((t) => !transformIds.includes(t)),
        (topicId) => {
            const fullTopicId = findFullTopicId(topicId);
            const split = fullTopicId.split('.');
            const head = split.shift();

            // Clean topic name for display (remove .default for DynamoDB only if original pipeline topic doesn't contain .default)
            let displayName = split.length ? split.join('.') : head;
            if (pipelineSource.value?.connector === 'dynamodb' && split.length > 0 && split[0] === 'default') {
                // Only strip .default if the original pipeline topicId doesn't also contain .default
                const originalPipelineTopicContainsDefault = topicId && topicId.includes('.default');
                if (!originalPipelineTopicContainsDefault) {
                    displayName = split.slice(1).join('.') || head;
                }
            }

            return {
                id: split.length ? head : '-',
                name: displayName,
                topic_id: fullTopicId,
                topic: true
            };
        }
    );

    return [...transforms, ...topics];
});

/**
 * Copy ID to clipboard
 */
const onCopyId = async (id) => {
    try {
        await navigator.clipboard.writeText(id);
        toast.success('Copied!', {
            description: `ID "${id}" copied to clipboard`
        });
    } catch (error) {
        console.error('Failed to copy ID:', error);
        toast.error('Failed to copy ID to clipboard');
    }
};

/**
 * Navigate to topic details if topic_id exists
 */
const goToTopic = (item) => {
    if (item.topic_id) {
        router.push({ name: 'topic-details', params: { topic_id: item.topic_id } });
    }
};

const currentTab = registerFilter('tab', FILTER_DEFAULTS.TABS.STATUS);

const setCurrentTab = (str) => {
    if (str === 'Source' || str === 'Destination') {
        return;
    }

    currentTab.value = str;
};

const updateCanSaveTree = (newValue) => {
    canSaveTree.value = newValue;
};

// Auto-discovery state - tracks which transforms have auto-discovery enabled
// Stored as Set of transform IDs for efficient lookups
const autoDiscoveryTransforms = ref(new Set());

// Default auto-discovery state from server
let defaultAutoDiscoveryTransforms = ref(new Set());

/**
 * Creates input configuration for transform tree
 * Returns configuration object for TreeView :inputs prop
 * Currently no inputs configured - structure kept for future extensibility
 */
const createTransformInputsConfig = () => {
    return {
        global: [],
        group: [
            {
                type: 'checkbox',
                key: 'auto-discovery',
                label: 'Enable auto-discovery of new topics',
                tooltip: 'When this transform produces new output topics, they will be automatically added to this pipeline',
                value: (transformId) => {
                    // Check if this transform ID is in the enabled set
                    return autoDiscoveryTransforms.value.has(transformId);
                }
            }
        ],
        item: []
    };
};

// Reactive input configuration
const transformInputsConfig = computed(() => createTransformInputsConfig());

/**
 * Handle transform input changes
 * Updates the auto-discovery state when checkboxes are toggled
 */
const handleTransformInputChange = (event) => {
    const { level, key, value, nodeId } = event;

    if (key === 'auto-discovery' && level === 'group') {
        // nodeId is the transform ID (since we group by ID)
        if (value) {
            autoDiscoveryTransforms.value.add(nodeId);
        } else {
            autoDiscoveryTransforms.value.delete(nodeId);
        }
        // Trigger reactivity by creating a new Set reference
        autoDiscoveryTransforms.value = new Set(autoDiscoveryTransforms.value);
    }
};

function onStatusClick(connector) {
    if (connector.connector_status === 'Broken' && connector.task_statuses) {
        const errors = [...new Set(Object.values(connector.task_statuses).map((task) => task.trace))];
        useErrorDialog({
            errors,
            width: 65
        });
    }
}

function selectedConnectorPath(id, type = 'source') {
    return type === 'source' ? { name: 'source-details', params: { id } } : { name: 'destination-details', params: { id } };
}

onMounted(async () => {
    // Set all loading states initially
    generalLoading.value = true;
    configurationSchemaLoading.value = true;
    transformsLoading.value = true;
    metricsLoading.value = true;
    tagsLoading.value = true;

    try {
        // Start all independent API calls immediately in parallel for maximum performance
        const coreDataPromise = getPipelineDetails(props.id, route?.query?.customMessage);
        const tagsPromise = tagsStore.fetch();
        const transformsPromise = transformsStore.fetchForConnectors();
        const connectorsPromise = sourcesStore.fetchConnectors();

        // Wait for core pipeline data and enable basic UI immediately
        const pipelineFetch = await coreDataPromise;
        pipeline.value = pipelineFetch;
        generalLoading.value = false; // Enable basic UI as soon as pipeline loads

        // Initialize tags if they're already loaded
        initializeSelectedTags();

        // Initialize default values if dependencies are already loaded
        initializeDefaultValues();

        // Start source loading using consolidated logic (no more if/else duplication)
        const sourceId = props.source || pipeline.value?.source?.id;
        const sourcePromise = sourceId ? sourcesStore.getById(sourceId) : Promise.resolve(null);

        // Process tags as soon as available (non-blocking UI rendering)
        tagsPromise.then(() => {
            tags.value = tagsStore.tagsPerTypeForEntityCRUD[TAG_TYPES.PIPELINE];
            initializeSelectedTags();
        });

        // Process transforms as soon as available (non-blocking UI rendering)
        transformsPromise.then(() => {
            if (transformsStore.allForConnectors && !_.isEmpty(transformsStore.allForConnectors)) {
                constructTransformTreeNodes();
            }
            transformsLoading.value = false; // Enable transforms section immediately
        });

        // Start metrics loading immediately (non-blocking UI rendering)
        const metricsPromise = pipeline.value?.id
            ? metricsStore.pipelines
                  .fetch(pipeline.value.id)
                  .then(() => {
                      metricsLoading.value = false; // Enable metrics section immediately
                  })
                  .catch((e) => {
                      console.error('Error loading pipeline metrics:', e);
                      metricsLoading.value = false; // Enable section even on error
                  })
            : Promise.resolve().then(() => {
                  metricsLoading.value = false;
              });

        // Wait for source data and assign it
        const source = await sourcePromise;
        pipelineSource.value = source;

        // Start configuration loading and tree processing
        const configurationPromise = pipeline.value?.source?.connector
            ? sourcesStore.fetchConfiguration(pipeline.value.source.connector).then(() => {
                  loadTreeViews();
                  configurationSchemaLoading.value = false; // Enable schema section as soon as ready
              })
            : Promise.resolve().then(() => {
                  configurationSchemaLoading.value = false;
              });

        // Set up default values as soon as essential data is available (non-blocking)
        Promise.all([tagsPromise, transformsPromise, connectorsPromise]).then(() => {
            initializeDefaultValues();
        });

        // Set up WebSocket listeners immediately (doesn't need to wait)
        addWebsocketListeners();

        // Wait for remaining heavy operations to complete (for cleanup)
        await Promise.all([configurationPromise, metricsPromise]);
    } catch (error) {
        console.error('Error loading pipeline details:', error);
        // Set loading states to false even on error to prevent infinite loading
        generalLoading.value = false;
        tagsLoading.value = false;
        transformsLoading.value = false;
        configurationSchemaLoading.value = false;
        metricsLoading.value = false;
    } finally {
        // Ensure all loading states are cleared
        generalLoading.value = false;
        configurationSchemaLoading.value = false;
        transformsLoading.value = false;
        metricsLoading.value = false;
        tagsLoading.value = false;
    }
});

const addWebsocketListeners = () => {
    WebSocketService.addListener({
        topic: 'metrics',
        entityType: 'pipelines',
        entityId: props.id,
        callback: metricsStore.onMetricsData,
        callbackId: 'metrics.onMetricsData'
    });
};

const removeWebsocketListeners = () => {
    WebSocketService.removeListener({
        topic: 'metrics',
        entityType: 'pipelines',
        entityId: props.id,
        callbackId: 'metrics.onMetricsData'
    });
};

onBeforeUnmount(() => {
    removeWebsocketListeners();
    clearDynamicLabel('pipeline-details');
});

async function getPipelineDetails(id, customMsg) {
    var result = await pipelinesStore.getById(id, customMsg);

    if (customMsg && result) {
        router.replace({ query: { ...route.query, customMessage: undefined } });
    }

    return result;
}

// Helper function to safely initialize selected tags (handles race conditions)
function initializeSelectedTags() {
    if (pipeline.value && tags.value) {
        // Handle case where pipeline.tags might be undefined or empty
        const pipelineTags = pipeline.value.tags || [];
        selectedTags.value = tags.value.filter((tag) => pipelineTags.includes(tag.id || tag._id));
    }
    // Don't set tagsLoading to false if only tags are loaded but no pipeline yet
}

// Helper function to safely initialize default values (handles race conditions)
function initializeDefaultValues() {
    if (pipeline.value && tags.value) {
        defaultPipelineName.value = pipeline.value.name || '';
        defaultPipelineTags.value = tags.value?.filter((tag) => pipeline.value.tags?.includes(tag.id || tag._id));

        // Initialize periodic audit config from existing pipeline data
        periodicAuditConfig.value = pipeline.value.periodic_audit
            ? {
                  enabled: true,
                  topics: pipeline.value.periodic_audit.topics || [],
                  timestampColumn: pipeline.value.periodic_audit.timestamp_column || '',
                  intervalMinutes: pipeline.value.periodic_audit.interval_minutes || null
              }
            : { enabled: false, topics: [], timestampColumn: '', intervalMinutes: null };

        defaultAuditConfig.value = _.cloneDeep(periodicAuditConfig.value);

        // Initialize auto-discovery transforms from pipeline data
        const transformIdsFromPipeline = (pipeline.value.topic_auto_discovery_transforms || []).map((item) =>
            typeof item === 'string' ? item : item.transform_id
        );
        autoDiscoveryTransforms.value = new Set(transformIdsFromPipeline);
        defaultAutoDiscoveryTransforms.value = new Set(transformIdsFromPipeline);
    }
}

/**
 * Resets all default values to current state
 * Used after save to sync defaults with the saved pipeline state
 */
function resetDefaultsToCurrentState() {
    // Update basic defaults
    defaultPipelineName.value = pipeline.value.name;
    defaultPipelineTags.value = [...selectedTags.value];
    defaultAuditConfig.value = _.cloneDeep(periodicAuditConfig.value);
    defaultAutoDiscoveryTransforms.value = new Set(autoDiscoveryTransforms.value);

    // Update tree defaults
    defaultSchemaTree.value = _.cloneDeep({
        nodes: treeNodes.value,
        levels: treeLevels.value
    });
    defaultTransformsTree.value = _.cloneDeep({
        nodes: transformTreeNodes.value,
        levels: transformTreeLevels.value
    });

    // Manually recalculate tree equality since watchers won't trigger
    const transformTreeValid = checkTransformTreeValidity();
    const schemaTreeValid = checkSchemaTreeValidity(transformTreeValid.valid);
    canSaveTree.value = schemaTreeValid.valid && transformTreeValid.valid;
    areTreesEqual.value = schemaTreeValid.equal && transformTreeValid.equal;
}
function loadTreeViews() {
    if (!pipelineSource.value) {
        return;
    }

    try {
        pipelineSourceConfig.value = sourcesStore.configurations[pipeline.value.source.connector];

        if (!pipelineSourceConfig.value) {
            return;
        }

        // Step 1: Construct tree nodes first
        constructTreeNodes();

        // Step 2: Ensure tree nodes were created successfully
        if (!treeNodes.value || treeNodes.value.length === 0) {
            return;
        }

        // Step 3: Select only the topics this pipeline actually uses (not all source topics)
        const topics = formattedTopicsAndTransforms.value?.filter((x) => x.topic);

        if (topics && topics.length > 0) {
            const topicsForSelection = topics.map((t) => ({
                name: t.name,
                topic_id: t.topic_id
            }));
            setSelectedTopics(treeNodes.value, topicsForSelection, '');
        } else {
            // For pipelines with transforms but no direct topics, try matching pipeline topics to source schema
            if (pipeline.value?.topics && pipeline.value.topics.length > 0) {
                const pipelineTopicsForSelection = pipeline.value.topics.map((topicName) => {
                    // Find matching source topic for this pipeline topic name
                    const matchingSourceTopic = pipelineSource.value?.topic_ids?.find((sourceTopicId) => {
                        const parts = sourceTopicId.split('.');
                        const sourceSuffix = parts.slice(-2).join('.'); // e.g., 'live.TrialSummaries'
                        return sourceSuffix.includes(topicName) || topicName.includes(sourceSuffix);
                    });

                    return {
                        name: topicName,
                        topic_id: matchingSourceTopic || topicName
                    };
                });

                setSelectedTopics(treeNodes.value, pipelineTopicsForSelection, '');
            }
        }

        // Step 4: Save default state
        defaultSchemaTree.value = _.cloneDeep({
            nodes: treeNodes.value,
            levels: treeLevels.value
        });
    } finally {
        transformsLoading.value = false;
    }
}

function setSelectedTopics(nodes, topics, parentName) {
    let isSelected = false;

    for (let node of nodes) {
        let fullName = parentName ? parentName + '.' + node.name : node.name;

        // Find matching topic using multiple strategies - ALL EXACT MATCHING
        const matchingTopic = topics.find((topic) => {
            // Strategy 1: Exact match on topic_id or name
            if (topic.topic_id === fullName || topic.name === fullName) {
                return true;
            }

            // Strategy 2: For leaf nodes, match against node name directly (exact)
            if (!node.children || node.children.length === 0) {
                if (topic.topic_id === node.name || topic.name === node.name) {
                    return true;
                }
            }

            // Strategy 3: Check if topic_id ends with current path (exact hierarchical match)
            if (topic.topic_id && topic.topic_id.endsWith('.' + node.name)) {
                // Additional validation: ensure it's a proper path segment, not partial match
                const beforeDot = topic.topic_id.substring(0, topic.topic_id.length - ('.' + node.name).length);
                if (beforeDot === '' || beforeDot.endsWith('.')) {
                    return true;
                }
            }

            // Strategy 4: Check for exact path segment matches in hierarchical topics
            if (topic.topic_id && fullName.includes('.')) {
                // Split both topic_id and fullName into segments and compare
                const topicSegments = topic.topic_id.split('.');
                const fullNameSegments = fullName.split('.');

                // Check if fullName segments exactly match consecutive segments in topic_id
                for (let i = 0; i <= topicSegments.length - fullNameSegments.length; i++) {
                    let matches = true;
                    for (let j = 0; j < fullNameSegments.length; j++) {
                        if (topicSegments[i + j] !== fullNameSegments[j]) {
                            matches = false;
                            break;
                        }
                    }
                    if (matches) {
                        return true;
                    }
                }
            }

            return false;
        });

        if (matchingTopic) {
            node.isSelected = true;
            isSelected = true;
        }

        // Recursively process children
        if (node.children && node.children.length > 0) {
            if (setSelectedTopics(node.children, topics, fullName)) {
                node.isSelected = true;
                isSelected = true;
            }
        }
    }

    return isSelected;
}

const getFormattedValue = (metricKey, unit, dashOnRule = null) => {
    const metricValue = pipelineLatestMetrics.value?.inline?.[metricKey];

    const formatterParams = [metricValue, { unit }];

    if (dashOnRule || dashOnRule === false) {
        formatterParams.push(dashOnRule);
    }

    return formatter(...formatterParams);
};

const constructTreeNodes = () => {
    var configProps = pipelineSource.value.config;
    var sourceConnector = Object.values(sourcesStore.sourceConnectors)
        .flat()
        .find((x) => x.connector == pipelineSource.value.connector);

    const nodes = buildTreeNodesOutOfExistingConfig(configProps, pipelineSourceConfig.value, sourceConnector, false);

    treeLevels.value = nodes.treeLevels;
    treeNodes.value = nodes.treeNodes;

    return { nodes: treeNodes.value, levels: treeLevels.value };
};

const checkSchemaTreeValidity = (transformTreeValid = false) => {
    if (_.isEmpty(treeNodes.value) && _.isEmpty(treeLevels.value)) {
        return {
            valid: true,
            equal: true
        };
    }

    let valid = transformTreeNodes.value.some((x) => x.isSelected) ? transformTreeValid : false;

    let equal = false;
    const areNodesEqual = compareWithOmittedProperties(defaultSchemaTree.value.nodes, treeNodes.value, ['collapsed']);
    const areLevelsEqual = compareWithOmittedProperties(defaultSchemaTree.value.levels, treeLevels.value, ['collapsed']);

    if (treeNodes.value.some((x) => x.isSelected)) {
        valid = checkAllSelectedHaveSelectedChild(treeNodes.value);
    } else {
        // the schema tree is valid when nothing is selected only if transform tree has selected stuff (we can't have both empty)
        valid = checkPartialSelectedHaveSomeChild(transformTreeNodes.value);
    }

    equal = areNodesEqual && areLevelsEqual;

    return {
        valid,
        equal
    };
};

const checkTransformTreeValidity = (schemaTreeValid = false) => {
    if (_.isEmpty(transformTreeNodes.value) && _.isEmpty(transformTreeLevels.value)) {
        return {
            valid: true,
            equal: true
        };
    }

    let valid = treeNodes.value.some((x) => x.isSelected) ? schemaTreeValid : false;
    let equal = false;

    const areNodesEqual = compareWithOmittedProperties(defaultTransformsTree.value.nodes, transformTreeNodes.value, ['collapsed']);
    const areLevelsEqual = compareWithOmittedProperties(defaultTransformsTree.value.levels, transformTreeLevels.value, ['collapsed']);

    // Check for selections at ANY depth in the tree, not just top level
    const hasAnySelections = (nodes) => {
        for (const node of nodes) {
            if (node.isSelected) return true;
            if (node.children && hasAnySelections(node.children)) return true;
        }
        return false;
    };

    const hasSelections = hasAnySelections(transformTreeNodes.value);

    if (hasSelections) {
        valid = checkAllSelectedHaveSelectedChild(transformTreeNodes.value);
    } else {
        // the transform tree is valid when nothing is selected only if schema tree has selected stuff (we can't have both empty)
        valid = checkPartialSelectedHaveSomeChild(treeNodes.value);
    }

    equal = areNodesEqual && areLevelsEqual;

    return {
        valid,
        equal
    };
};

const nameChanged = computed(() => {
    return pipeline.value?.name && pipeline.value?.name !== defaultPipelineName.value;
});

const tagsChanged = computed(() => {
    return !_.isEqual(selectedTags.value, defaultPipelineTags.value);
});

const auditChanged = computed(() => {
    return !_.isEqual(periodicAuditConfig.value, defaultAuditConfig.value);
});

const autoDiscoveryChanged = computed(() => {
    // Compare current set with default set
    const current = Array.from(autoDiscoveryTransforms.value).sort();
    const defaults = Array.from(defaultAutoDiscoveryTransforms.value).sort();
    return !_.isEqual(current, defaults);
});

watch(
    treeNodes,
    () => {
        const transformTreeValid = checkTransformTreeValidity();
        const validity = checkSchemaTreeValidity(transformTreeValid.valid);

        canSaveTree.value = validity.valid;
        areTreesEqual.value = validity.equal && checkTransformTreeValidity(validity.valid).equal;
    },
    { deep: true }
);

watch(
    transformTreeNodes,
    () => {
        const schemaTreeValid = checkSchemaTreeValidity();
        const validity = checkTransformTreeValidity(schemaTreeValid.valid);

        canSaveTree.value = validity.valid;
        areTreesEqual.value = validity.equal && checkSchemaTreeValidity(validity.valid).equal;
    },
    { deep: true }
);

const checkAllSelectedHaveSelectedChild = (nodes) => {
    for (let obj of nodes) {
        if (obj.isSelected && obj.children && obj.children?.length > 0) {
            if (!obj.children?.some((child) => child.isSelected)) {
                return false;
            }
        }

        if (obj.isSelected && (!obj.children || !obj.children?.length)) {
            return true;
        }

        if (obj.children) {
            const childResult = checkAllSelectedHaveSelectedChild(obj.children);
            if (!childResult) {
                return false;
            }
        }
    }

    return true;
};

const checkPartialSelectedHaveSomeChild = (nodes) => {
    for (let obj of nodes) {
        if (obj.isSelected) {
            return true;
        }

        if (obj.children && obj.children?.length > 0) {
            if (obj.children?.some((child) => child.isSelected)) {
                return true;
            }
        }

        if (obj.children) {
            const childResult = checkPartialSelectedHaveSomeChild(obj.children);

            if (!childResult) {
                return false;
            }
        }
    }

    return false;
};

// Reactive list of selected source schema topics (leaf nodes)
const computedSourceTopics = computed(() => {
    const out = [];
    const collect = (nodes, prefix = '') => {
        nodes.forEach((node) => {
            const fullName = prefix ? `${prefix}.${node.name}` : node.name;
            if ((!node.children || node.children.length === 0) && node.isSelected) {
                out.push(fullName);
            }
            if (node.children) {
                collect(node.children, fullName);
            }
        });
    };
    collect(treeNodes.value, '');
    return out;
});

const constructTransformTreeNodes = () => {
    transformTreeLevels.value.push({ schemaLevel: 'schema', schemaObject: 'random' }, { schemaLevel: 'topic', schemaObject: 'random' });
    transformTreeNodes.value = [];
    const children = [];

    transformsStore.allForConnectors.forEach((transform) => {
        const isTransformIncluded = !!pipeline.value.transforms.find(
            (t) => t.name === transform.name && t.id === transform.id && t.topic_id === transform.topic_id
        );

        children.push({
            id: transform.id,
            transformName: transform.name,
            name: transform.topic,
            isSelected: isTransformIncluded,
            schemaLevel: 'topic',
            schemaObject: transform
        });
    });

    transformTreeNodes.value.push({
        name: 'transforms',
        isSelected: children?.filter((c) => c.isSelected).length === _.size(transformsStore.allForConnectors),
        collapsed: false,
        schemaLevel: 'schema',
        schemaObject: 'random',
        children
    });

    defaultTransformsTree.value = _.cloneDeep({
        nodes: transformTreeNodes.value,
        levels: transformTreeLevels.value
    });
};

const getSelectedTransformTreeNodes = () => {
    if (transformTreeNodes.value.length === 0) {
        return [];
    }
    return transformTreeNodes.value[0].children.filter((topicNode) => topicNode.isSelected).map((topicNode) => topicNode.schemaObject);
};

onBeforeRouteLeave((to, from, next) => {
    if (generalLoading.value || actionLoading.value || configurationSchemaLoading.value || transformsLoading.value || metricsLoading.value) {
        return next();
    }

    if (
        hidePromptTemporary.value ||
        (areTreesEqual.value && !nameChanged.value && !!pipeline.value.name && !tagsChanged.value && !autoDiscoveryChanged.value && !auditChanged.value)
    ) {
        return next();
    }

    const answer = window.confirm('Are you sure you want to leave? You have unsaved changes.');

    if (answer) {
        return next();
    } else {
        return next(false);
    }
});

const onDelete = async () => {
    try {
        await useDeleteConfirmation({
            entity: 'pipeline',
            name: pipeline.value.name,
            title: 'Delete Pipeline',
            message: `Are you sure you want to delete the pipeline <strong>"${pipeline.value.name}"</strong>? This action cannot be undone and you will be redirected to the pipelines page.`,
            onConfirm: async () => {
                actionLoading.value = true;
                try {
                    await pipelinesStore.delete(pipeline.value.id);
                    pipelinesStore.fetch();
                    router.push({ name: 'pipelines' });
                } finally {
                    actionLoading.value = false;
                }
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Delete failed:', error);
        }
    }
};

const handleTagSelection = () => {
    const existingExclusiveTag = selectedTags.value.filter((tag) => tag.type?.length && MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));

    if (existingExclusiveTag?.length > 1) {
        toast.warning(`Only one tag of the same ${MAX_ONE_TAG_PER_TYPE.join(', ')} type can be selected`);

        selectedTags.value = selectedTags.value.filter((tag) => tag.type?.length && !MAX_ONE_TAG_PER_TYPE.some((type) => tag.type.includes(type)));
        selectedTags.value.push(existingExclusiveTag[0]);
        return;
    }
};

// Watch for changes in selectedTags to handle tag selection validation
watch(
    selectedTags,
    () => {
        handleTagSelection();
    },
    { deep: true }
);

const onSavePipeline = () => {
    actionLoading.value = true;

    if (defaultPipelineTags.value?.length && !selectedTags.value?.length) {
        toast.error('Please select at least one tag. Having at least an environment-related tag (Production/Development) is mandatory.');

        actionLoading.value = false;
        return;
    }

    if (!selectedTags.value?.find((tag) => tag.type?.includes(TAG_TYPES.ENVIRONMENT))) {
        toast.error('Please select at least one environment-related tag (Production/Development).');

        actionLoading.value = false;
        return;
    }

    const auditPayload = periodicAuditConfig.value.enabled
        ? {
              topics: periodicAuditConfig.value.topics,
              timestamp_column: periodicAuditConfig.value.timestampColumn,
              interval_minutes: periodicAuditConfig.value.intervalMinutes
          }
        : null;

    pipelinesStore
        .edit(
            {
                name: pipeline.value.name,
                snapshot_new_tables: !!snapshotNewTables.value,
                destination: {
                    connector: pipeline.value.destination.connector,
                    name: pipeline.value.destination.name,
                    id: pipeline.value.destination.id
                },
                source: {
                    connector: pipeline.value.source.connector,
                    name: pipeline.value.source.name,
                    topics: computedSourceTopics.value,
                    id: pipeline.value.source.id
                },
                transforms: getSelectedTransformTreeNodes(),
                tags: selectedTags.value?.map((tag) => tag.id || tag._id),
                periodic_audit: auditPayload,
                topic_auto_discovery_transforms: Array.from(autoDiscoveryTransforms.value).map((transformId) => ({
                    transform_id: transformId,
                    regex: '.*'
                }))
            },
            props.id
        )
        .then(async (updatedPipelineData) => {
            hidePromptTemporary.value = true;

            // Update current pipeline object with the response data
            Object.assign(pipeline.value, updatedPipelineData);

            // Wait for all reactive updates to complete
            await nextTick();
            await nextTick();

            // Reset all defaults to match the new saved state
            resetDefaultsToCurrentState();

            // Show success notification
            toast.success('Pipeline updated successfully.');
            // Stay on current pipeline details page - no redirect
        })
        .catch((err) => {
            formError.value = err;
        })
        .finally(() => {
            actionLoading.value = false;
            hidePromptTemporary.value = false;
        });
};

const onSnapshot = async () => {
    actionLoading.value = true;

    try {
        await useConfirmDialog({
            type: 'snapshot',
            title: 'Trigger Snapshot',
            message: `Are you sure you want to trigger a snapshot on all tables for <strong>"${pipeline.value.name}"</strong>?`,
            confirmText: 'Trigger Snapshot',
            requiresInput: true,
            inputPlaceholder: 'Type "snapshot" to confirm',
            inputExpectedValue: 'snapshot',
            onConfirm: async () => {
                const snapshotBody = {
                    source_id: pipeline.value.source.id,
                    connector: pipeline.value.source.connector,
                    topic_names: pipeline.value.topics
                };

                await sourcesStore.snapshot(snapshotBody);
            }
        });
    } catch (error) {
        // User cancelled or error occurred
        if (error.message) {
            console.error('Snapshot failed:', error);
        }
    } finally {
        actionLoading.value = false;
    }
};

const onCancelSnapshot = async () => {
    actionLoading.value = true;

    try {
        const snapshotBody = {
            source_id: pipeline.value.source.id,
            connector: pipeline.value.source.connector,
            topic_names: pipeline.value.topics
        };

        await sourcesStore.cancelSnapshot(snapshotBody);
    } finally {
        actionLoading.value = false;
    }
};

const canConnectorCancelSnapshot = () => {
    // Check if snapshot is disabled via global config
    const snapshotDisabled = getGlobalConfigValue(pipelineSourceConfig.value, 'incremental.snapshot.disabled');

    // If global config is not found (null), assume snapshots are enabled (false = not disabled)
    // If global config is found and true, then snapshots are disabled
    if (snapshotDisabled === true) return false;

    let canIt = true;

    // if (NO_SNAPSHOT_CONNECTORS.includes(pipeline.value?.source?.connector) || !pipeline.value?.topics?.length) return false;

    return canIt;
};

const canConnectorSnapshot = () => {
    // Check if snapshot is disabled via global config
    const snapshotDisabled = getGlobalConfigValue(pipelineSourceConfig.value, 'incremental.snapshot.disabled');

    // If global config is not found (null), assume snapshots are enabled (false = not disabled)
    // If global config is found and true, then snapshots are disabled
    if (snapshotDisabled === true) return false;

    // if (NO_SNAPSHOT_CONNECTORS.includes(pipeline.value?.source?.connector) || !pipeline.value?.topics?.length) return false;

    return true;
};

const handleTopicsConfirmModal = async (shouldSnapshot) => {
    if (_.isBoolean(shouldSnapshot)) {
        snapshotNewTables.value = shouldSnapshot;

        await onSavePipeline();
    }
};
</script>

<template>
    <div>
        <div class="page-header">
            <PageBreadcrumb>
                <template #actions>
                    <ActionMenu
                        v-if="!generalLoading"
                        variant="menu"
                        menu-trigger-size="md"
                        :show-stop-source="userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showStopSource"
                        :show-stop-destination="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showStopDestination"
                        :show-resume-source="userHasAccess([$Permissions.WRITE_SOURCES], authStore.user) && showResumeSource"
                        :show-resume-destination="userHasAccess([$Permissions.WRITE_DESTINATIONS], authStore.user) && showResumeDestination"
                        :show-delete="userHasAccess([$Permissions.DELETE_PIPELINES], authStore.user)"
                        :show-snapshot="canConnectorSnapshot()"
                        :show-cancel-snapshot="canConnectorCancelSnapshot()"
                        :show-copy-id="true"
                        :loading="actionLoading"
                        :disabled="generalLoading || actionLoading"
                        @stopSource="onStopSource"
                        @resumeSource="onResumeSource"
                        @delete="onDelete"
                        @snapshot="onSnapshot"
                        @cancelSnapshot="onCancelSnapshot"
                        @stopDestination="onStopDestination"
                        @resumeDestination="onResumeDestination"
                        @copyId="onCopyId(pipeline.id)" />
                </template>
            </PageBreadcrumb>
        </div>
        <div>
            <AppLoader :force-loading="generalLoading" default-height="60vh" class="min-h-60">
                <div v-if="pipeline" class="w-full mb-5">
                    <Tabs :tabs="tabs" :initial-active-tab="currentTab" :loading="generalLoading" @tab-change="setCurrentTab">
                        <template #Status>
                            <div class="mt-5">
                                <div class="w-full mt-3 mb-5">
                                    <h3 class="mb-3 font-medium">Tags</h3>
                                    <div class="flex flex-wrap gap-2">
                                        <!-- Skeleton loading -->
                                        <template v-if="tagsLoading || generalLoading">
                                            <SkeletonLoader preset="badge" :loading="true" />
                                            <SkeletonLoader preset="badge" :loading="true" />
                                            <SkeletonLoader preset="badge" :loading="true" />
                                        </template>
                                        <!-- Actual tags -->
                                        <template v-else>
                                            <Badge
                                                v-for="tag in tags.filter((tag) => pipeline.tags.includes(tag.id || tag._id))"
                                                :key="tag.id || tag._id"
                                                variant="outline"
                                                :class="
                                                    TAG_BADGE_COLOR_CLASSES_PER_TYPE[tag.type?.find((type) => type === 'pipelines') || tag.type[0]]
                                                ">
                                                {{ tag.name }}
                                            </Badge>
                                            <div v-if="!pipeline.tags?.length" class="text-muted-foreground text-sm">
                                                No tags found. Add them in Settings.
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <div class="w-full mt-3">
                                    <h3 class="mb-3 font-medium">Stats</h3>

                                    <div class="mt-3 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                                        <template v-for="item in stats" :key="item.id">
                                            <!-- Custom Latency Card with High Latency Indicator -->
                                            <div v-if="item.name === 'Latency'" class="rounded-lg border bg-card text-card-foreground shadow-sm">
                                                <div class="flex flex-row items-center justify-between space-y-0 p-6 pb-2">
                                                    <div class="flex items-center space-x-2">
                                                        <h3 class="text-sm font-medium tracking-tight">
                                                            {{ item.name }}
                                                        </h3>
                                                    </div>
                                                    <Clock class="h-4 w-4 text-muted-foreground" />
                                                </div>
                                                <div class="p-6 pt-0">
                                                    <SkeletonLoader preset="stat-card" :loading="metricsLoading || configurationSchemaLoading" />
                                                    <div v-if="!metricsLoading && !configurationSchemaLoading" class="flex items-center gap-2">
                                                        <div class="text-2xl font-bold">{{ item.stat }}</div>
                                                        <Clock v-if="item.hasHighLatency" class="h-5 w-5 text-chart-1" title="High Latency" />
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Regular StatCard for other items -->
                                            <StatCard
                                                v-else
                                                :icon-bg-class="item.colorClass"
                                                :info-icon="item.infoIcon"
                                                :errors="item.errors"
                                                :icon="item.icon"
                                                :loading="metricsLoading || configurationSchemaLoading"
                                                :title="item.name"
                                                :value="item.stat" />
                                        </template>
                                    </div>
                                </div>
                                <div class="grid grid-cols-1 lg:grid-cols-2 w-full gap-4 mt-5">
                                    <!-- Source Card -->
                                    <div class="w-full">
                                        <div class="font-semibold flex items-center gap-3 text-md mb-3">Source</div>
                                        <div class="relative rounded-lg shadow-md p-4 border bg-card min-h-[6rem] flex flex-col justify-center">
                                            <div class="space-y-3">
                                                <!-- Name and Avatar Row -->
                                                <router-link :to="selectedConnectorPath(pipeline.source.id, 'source')" class="block">
                                                    <div class="flex items-center gap-3 group">
                                                        <Avatar
                                                            class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                            <AvatarImage
                                                                :src="`/icons/${pipeline.source.connector}.svg`"
                                                                alt=""
                                                                class="object-contain p-1" />
                                                            <AvatarFallback
                                                                class="bg-muted dark:bg-slate-200 dark:text-slate-800 text-muted-foreground font-semibold text-xs rounded-md">
                                                                {{ pipeline.source.connector?.charAt(0).toUpperCase() }}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div class="flex-1 min-w-0">
                                                            <div
                                                                class="font-medium text-sm group-hover:text-primary transition-colors duration-200 truncate"
                                                                :title="pipeline.source.name">
                                                                {{ pipeline.source.name }}
                                                            </div>
                                                            <div class="text-xs text-muted-foreground">
                                                                {{ getConnectorDisplayName(pipeline.source.connector) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </router-link>
                                                <!-- Badges Row -->
                                                <div class="flex items-center gap-2 flex-wrap">
                                                    <Badge variant="outline" class="text-xs">
                                                        {{ getConnectorDisplayName(pipeline.source.connector) }}
                                                    </Badge>
                                                    <Badge
                                                        :variant="getBadgeVariantAndClasses(pipeline.source.connector_status).variant"
                                                        :class="[
                                                            getBadgeVariantAndClasses(pipeline.source.connector_status).classes,
                                                            pipeline.source.connector_status === 'Broken' ? 'cursor-pointer hover:opacity-80' : '',
                                                            'text-xs transition-opacity'
                                                        ]"
                                                        @click="() => onStatusClick(pipeline.source)">
                                                        {{ pipeline.source.connector_status }}
                                                        <Info v-if="pipeline.source.connector_status === 'Broken'" class="h-3 w-3 ml-1" />
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Destination Card -->
                                    <div class="w-full">
                                        <div class="font-semibold flex items-center gap-3 text-md mb-3">Destination</div>
                                        <div class="relative rounded-lg shadow-md p-4 border bg-card min-h-[6rem] flex flex-col justify-center">
                                            <div class="space-y-3">
                                                <!-- Name and Avatar Row -->
                                                <router-link :to="selectedConnectorPath(pipeline.destination.id, 'destination')" class="block">
                                                    <div class="flex items-center gap-3 group">
                                                        <Avatar
                                                            class="h-10 w-10 bg-background dark:bg-slate-200 border border-border rounded-md shrink-0">
                                                            <AvatarImage
                                                                :src="`/icons/${pipeline.destination.connector}.svg`"
                                                                alt=""
                                                                class="object-contain p-1" />
                                                            <AvatarFallback
                                                                class="bg-muted dark:bg-slate-200 dark:text-slate-800 text-muted-foreground font-semibold text-xs rounded-md">
                                                                {{ pipeline.destination.connector?.charAt(0).toUpperCase() }}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div class="flex-1 min-w-0">
                                                            <div
                                                                class="font-medium text-sm group-hover:text-primary transition-colors duration-200 truncate"
                                                                :title="pipeline.destination.name">
                                                                {{ pipeline.destination.name }}
                                                            </div>
                                                            <div class="text-xs text-muted-foreground">
                                                                {{ getConnectorDisplayName(pipeline.destination.connector) }}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </router-link>
                                                <!-- Badges Row -->
                                                <div class="flex items-center gap-2 flex-wrap">
                                                    <Badge variant="outline" class="text-xs">
                                                        {{ getConnectorDisplayName(pipeline.destination.connector) }}
                                                    </Badge>
                                                    <Badge
                                                        :variant="getBadgeVariantAndClasses(pipeline.destination.connector_status).variant"
                                                        :class="[
                                                            getBadgeVariantAndClasses(pipeline.destination.connector_status).classes,
                                                            pipeline.destination.connector_status === 'Broken'
                                                                ? 'cursor-pointer hover:opacity-80'
                                                                : '',
                                                            'text-xs transition-opacity'
                                                        ]"
                                                        @click="() => onStatusClick(pipeline.destination)">
                                                        {{ pipeline.destination.connector_status }}
                                                        <Info v-if="pipeline.destination.connector_status === 'Broken'" class="h-3 w-3 ml-1" />
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="w-full mt-5">
                                    <div class="font-semibold flex items-center gap-3 text-md mb-3 pr-3">Topics</div>
                                    <div class="rounded-md border shadow-md">
                                        <Table>
                                            <TableHeader class="bg-muted/50">
                                                <TableRow>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>Type</TableHead>
                                                    <TableHead class="w-24">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                <TableRow v-for="topicOrTransform in formattedTopicsAndTransforms" :key="topicOrTransform.id">
                                                    <TableCell class="font-medium">
                                                        <Button
                                                            v-if="topicOrTransform.topic_id"
                                                            variant="link"
                                                            class="h-auto p-0 max-w-full text-left justify-start font-medium"
                                                            :title="topicOrTransform.name"
                                                            @click="goToTopic(topicOrTransform)">
                                                            <span
                                                                class="truncate max-w-[200px] sm:max-w-[250px] md:max-w-[300px] lg:max-w-[350px] xl:max-w-[400px]">
                                                                {{ topicOrTransform.name }}
                                                            </span>
                                                        </Button>
                                                        <span v-else class="font-medium">
                                                            {{ topicOrTransform.name }}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>
                                                        <div class="gap-2 flex flex-col">
                                                            <div class="flex items-center gap-1 text-muted-foreground">
                                                                <List v-if="topicOrTransform.topic" class="h-4 w-4" />
                                                                <SlidersVertical v-else class="h-4 w-4" />
                                                                <span class="text-xs font-medium uppercase tracking-wider">
                                                                    {{ topicOrTransform.topic ? 'Topic' : 'Transform' }}
                                                                </span>
                                                            </div>
                                                            <Badge v-if="topicOrTransform.transform_name" variant="outline" class="text-xs">
                                                                Transform: {{ topicOrTransform.transform_name }}
                                                            </Badge>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <ActionMenu
                                                            variant="menu"
                                                            :show-copy-id="true"
                                                            @copyId="onCopyId(topicOrTransform.topic_id || topicOrTransform.id)" />
                                                    </TableCell>
                                                </TableRow>
                                                <TableEmpty v-if="!formattedTopicsAndTransforms?.length && !generalLoading && !transformsLoading">
                                                    <TableEmptyState message="No topics or transforms found" />
                                                </TableEmpty>
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            </div>
                        </template>

                        <template #Settings>
                            <div class="mt-5 w-full">
                                <div class="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 p-5 rounded-lg shadow-md space-y-6 bg-card">
                                    <div class="space-y-8 border-primary/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-border sm:pb-0">
                                        <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                            <Label for="name" class="block sm:pt-1.5"> Name</Label>
                                            <div class="flex items-center space-x-3">
                                                <Input
                                                    id="name"
                                                    v-model="pipeline.name"
                                                    :disabled="generalLoading || actionLoading || transformsLoading || configurationSchemaLoading"
                                                    name="name"
                                                    type="text"
                                                    placeholder="Your pipeline name"
                                                    autocomplete="name"
                                                    required />
                                            </div>
                                        </div>

                                        <div class="space-y-8 border-primary/10 pb-6 sm:space-y-0 sm:divide-y sm:divide-border sm:pb-0">
                                            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                                <Label for="name" class="block sm:pt-1.5 flex items-center">
                                                    Tags
                                                    <a
                                                        href="https://docs.streamkap.com/docs/streamkap-support"
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        class="ml-2 text-muted-foreground hover:text-accent-foreground transition-colors duration-200">
                                                        <HelpCircle class="w-5 h-5" />
                                                    </a>
                                                </Label>
                                                <div class="flex items-center space-x-3 mb-8">
                                                    <MultiSelect
                                                        v-model="selectedTagIds"
                                                        :options="tags"
                                                        :placeholder="
                                                            tagsLoading ? 'Loading...' : tags?.length ? 'Search for tags' : 'No tags available'
                                                        "
                                                        :disabled="generalLoading || actionLoading || tagsLoading || !tags?.length"
                                                        :option-label="'name'"
                                                        :track-by="'id'"
                                                        :class="validationClass" />
                                                </div>
                                            </div>
                                        </div>

                                        <!-- <div class="space-y-8 border-primary/10 pb-12 sm:space-y-0 sm:divide-y sm:divide-cyan-950/10 sm:pb-0">
                                            <div class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6">
                                                <Label for="name" class="block sm:pt-1.5">
                                                    Snapshot new topics
                                                </Label>
                                                <div class="flex items-center space-x-3">
                                                    <Switch
                                                        v-model="snapshotNewTables"
                                                        :class="[
                                                            snapshotNewTables ? 'bg-primary' : 'bg-neutral-300',
                                                            'relative inline-flex h-6 w-11 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out',
                                                            transformsLoading || configurationSchemaLoading ? 'disabled-toggle' : ''
                                                        ]"
                                                        :true-value="true"
                                                        :false-value="false"
                                                        :disabled="transformsLoading || configurationSchemaLoading"
                                                        aria-label="input"
                                                        aria-describedby="toggle">
                                                        <span
                                                            aria-hidden="true"
                                                            :class="[
                                                                snapshotNewTables ? 'translate-x-5' : 'translate-x-0',
                                                                'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-sm ring-0 transition duration-200 ease-in-out'
                                                            ]" />
                                                    </Switch>
                                                    <LoaderCircle v-if="transformsLoading || configurationSchemaLoading" class="h-4 w-4 animate-spin" />
                                                </div>
                                            </div>
                                        </div> -->

                                        <PeriodicAuditConfig
                                            v-model="periodicAuditConfig"
                                            :source-topics="computedSourceTopics"
                                            :tree-nodes="treeNodes"
                                            :tree-levels="treeLevels"
                                            :loading="transformsLoading || configurationSchemaLoading"
                                            v-if="isAuditAllowed && pipeline" />

                                        <div class="flex flex-col gap-3 py-6">
                                            <Label> Source Schema </Label>
                                            <div class="w-full">
                                                <div class="flex w-full rounded-lg border border-dashed border-primary/25 p-6">
                                                    <TreeView
                                                        :tree-nodes="treeNodes || []"
                                                        :tree-levels="treeLevels || []"
                                                        :loading="configurationSchemaLoading"
                                                        :allow-search="true"
                                                        class="w-full"
                                                        :disable-create-and-edit="true"
                                                        :custom-width="'100%'"
                                                        :edit-mode="true"
                                                        @can-save-duplicate="updateCanSaveTree" />

                                                    <div
                                                        v-if="
                                                            !configurationSchemaLoading &&
                                                            (!treeNodes || !treeNodes.length || !treeLevels || !treeLevels[0])
                                                        "
                                                        class="w-full">
                                                        <TableEmptyState :icon="Search" message="No schema available" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="flex flex-col gap-3 py-6">
                                            <Label> Transforms </Label>
                                            <div class="w-full">
                                                <div class="flex w-full rounded-lg border border-dashed border-primary/25 p-6">
                                                    <!-- Transform TreeView - Group by 'id' at depth 1 (topic level) -->
                                                    <TreeView
                                                        :tree-nodes="transformTreeNodes || []"
                                                        :tree-levels="transformTreeLevels || []"
                                                        :loading="transformsLoading"
                                                        class="w-full"
                                                        :custom-width="'100%'"
                                                        :allow-search="true"
                                                        :disable-create-and-edit="true"
                                                        :edit-mode="true"
                                                        :group-by="{ level: 1, key: 'id', labelKey: 'transformName' }"
                                                        :group-prefix="''"
                                                        :inputs="transformInputsConfig"
                                                        @can-save-duplicate="updateCanSaveTree()"
                                                        @input-change="handleTransformInputChange" />

                                                    <div
                                                        v-if="
                                                            !transformsLoading &&
                                                            (!transformTreeNodes ||
                                                                !transformTreeNodes.length ||
                                                                !transformTreeLevels ||
                                                                !transformTreeLevels[0])
                                                        "
                                                        class="w-full">
                                                        <TableEmptyState :icon="Search" message="No transforms found" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <ErrorMessage v-if="formError" :error="formError" :to-sentry="true" :context="'pipeline-details'" class="mt-3" />
                                </div>
                            </div>
                        </template>

                        <template #connect-guide>
                            <ConnectGuideTab :connect-guide="pipeline.connect_guide" />
                        </template>
                    </Tabs>
                </div>
            </AppLoader>
        </div>

        <!-- Footer will teleport to App.vue footer slot -->
        <div v-if="pipeline" v-can-access.permissions="[$Permissions.WRITE_PIPELINES]">
            <AppFooter>
                <template #right>
                    <ActionMenu
                        v-if="!generalLoading"
                        variant="row"
                        :show-save="true"
                        :show-discard="false"
                        :disable-save="
                            !selectedTags?.length ||
                            !pipeline?.name ||
                            (nameChanged ? !canSaveTree : false) ||
                            (tagsChanged ? !canSaveTree : false) ||
                            (!canSaveTree && !areTreesEqual) ||
                            (areTreesEqual && !nameChanged && !tagsChanged && !auditChanged && !autoDiscoveryChanged) ||
                            (periodicAuditConfig.enabled && !auditValid) ||
                            generalLoading ||
                            configurationSchemaLoading ||
                            transformsLoading
                        "
                        :loading="actionLoading"
                        :disabled="generalLoading || actionLoading"
                        @save="canConnectorSnapshot() ? (isTopicsConfirmModalOpen = true) : onSavePipeline()" />
                </template>
            </AppFooter>
        </div>
    </div>

    <YesNoConfirmModal
        v-show="isTopicsConfirmModalOpen && canConnectorSnapshot()"
        :is-open="isTopicsConfirmModalOpen"
        title="Snapshot New Topics?"
        @update:isOpen="isTopicsConfirmModalOpen = $event"
        @confirm="handleTopicsConfirmModal">
        <p class="text-sm text-muted-foreground">The save will proceed now. Would you also like to <b>snapshot the new topics</b>?</p>
    </YesNoConfirmModal>
</template>

<style></style>
